import { Box, createStyles, SimpleGrid, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { RpiAlert, RpiCheckboxV2 } from "@rpi/openapi-core";
import React from "react";
import { RpaButtonPromo } from "../../../components/buttons/RpaButton";
import { BraintreeInput } from "../../../components/inputFields/BraintreeInput";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { RpaNavLink } from "../../../components/RpiLink";
import { useBraintree } from "../../../hooks/useBraintree";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { countrySelectData } from "../../../utils/country.util";
import { usStateSelectData } from "../../../utils/usState.util";
import { PaymentFormValues, requiredField, usePaymentFormSubmission } from "./hooks/usePaymentFormSubmission";
import { Button, tokens } from "@blurb/codex-react";
import { RpaDrawer } from "../../../rpi-core/drawer/RpaDrawer";

const useDrawerStyles = createStyles({
  addPaymentDrawer: {
    padding: `0 ${tokens.spacing[6]}`,
  },
});

type PaymentMethodCreateViewProps = {
  drawerOpen: boolean;
  onClose: () => void;
};
export function PaymentMethodCreateView({ drawerOpen, onClose }: PaymentMethodCreateViewProps) {
  const { classes } = useDrawerStyles();
  return (
    <RpaDrawer title="Add payment method" opened={drawerOpen} onClose={onClose}>
      <div className={classes.addPaymentDrawer}>
        <CreatePaymentMethod onComplete={onClose} onCancel={onClose} />
      </div>
    </RpaDrawer>
  );
}

interface CreatePaymentMethodProps {
  isAccountSetup?: boolean;
  onComplete: () => void;
  onCancel?: () => void;
}

const FORM_ID = "payment-method-create-form";

const useStyles = createStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.md,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
  },
  skipLink: {
    textAlign: "center",
  },
}));

export const CreatePaymentMethod: React.FC<CreatePaymentMethodProps> = ({
  isAccountSetup = false,
  onComplete,
  onCancel,
}) => {
  const [countrySearchValue, setCountrySearchValue] = React.useState("");
  const [stateSearchValue, setStateSearchValue] = React.useState("");

  const { classes } = useStyles();

  const { fields, hostedFields, dataCollector, getPayload } = useBraintree();

  const form = useForm<PaymentFormValues>({
    initialValues: {
      cardholderName: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "",
      makeDefault: true,
      agreeToTerms: false,
    },

    validate: {
      cardholderName: requiredField("Please enter a valid name"),
      streetAddress: requiredField("Please enter a valid street address"),
      city: requiredField("Please enter a valid town/city"),
      state: (value: string | undefined, values: PaymentFormValues) =>
        values?.country === "US" && (value?.length ?? 0) === 0 ? "Please select a state" : null,
      country: requiredField("Please select a country"),
      agreeToTerms: (value: boolean) => (value ? null : "Please agree to the payment terms to continue"),
    },
  });

  const { isLoading, submit, cardError, braintreeErrors } = usePaymentFormSubmission({
    form,
    hostedFields,
    dataCollector,
    getPayload,
    onComplete,
  });

  return (
    <form
      id={FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className={classes.form}
    >
      <>
        {isAccountSetup && (
          <>
            <Title
              style={{
                fontSize: "24px",
                fontWeight: 600,
              }}
            >
              Let's set up your payment method
            </Title>
            <Text style={{ fontWeight: 600 }}>
              You will need to set up your payment method for the API integration. <br />
              You can also set this up later in Dashboard {">"} Account Settings.
            </Text>
          </>
        )}
        <RpaTextInput
          placeholder="Enter"
          required
          label="Name on Card"
          data-testid="cardholderName"
          disabled={isLoading}
          {...form.getInputProps("cardholderName")}
        />

        <BraintreeInput
          label="Credit Card Number"
          data-testid="cardNumber"
          isFocused={fields.number.isFocused}
          ref={fields.number.ref as any}
          onFocus={() => !isLoading && hostedFields?.focus("number")}
          error={braintreeErrors.number}
          errorMessage="Please enter a valid credit card number"
          disabled={isLoading}
        />

        <SimpleGrid cols={2} spacing={8}>
          <BraintreeInput
            label="Expiration Date"
            data-testid="expirationDate"
            isFocused={fields.expirationDate.isFocused}
            ref={fields.expirationDate.ref as any}
            onFocus={() => !isLoading && hostedFields?.focus("expirationDate")}
            error={!!braintreeErrors.expirationDate}
            errorMessage="Enter a valid expiration date"
            disabled={isLoading}
          />

          <BraintreeInput
            label="CVC"
            data-testid="cvc"
            isFocused={fields.cvv.isFocused}
            ref={fields.cvv.ref as any}
            onFocus={() => !isLoading && hostedFields?.focus("cvv")}
            error={!!braintreeErrors.cvv}
            errorMessage="CVC must be 3 or 4 digits"
            disabled={isLoading}
          />
        </SimpleGrid>

        <BraintreeInput
          label="Zip Code"
          data-testid="postalCode"
          isFocused={fields.postalCode.isFocused}
          ref={fields.postalCode.ref as any}
          onFocus={() => !isLoading && hostedFields?.focus("postalCode")}
          error={braintreeErrors.postalCode}
          errorMessage="Please enter a valid zip code"
          disabled={isLoading}
        />
        <Box
          sx={{
            width: "100%",
            height: 1,
            background: "#D1D1D1",
            margin: "16px 0",
          }}
        />

        <RpaTextInput
          placeholder="Enter"
          required
          label="Billing Street Address"
          data-testid="streetAddress"
          disabled={isLoading}
          {...form.getInputProps("streetAddress")}
        />
        <RpaTextInput
          placeholder="Enter"
          required
          label="Town/City"
          data-testid="city"
          disabled={isLoading}
          {...form.getInputProps("city")}
        />
        {form.values?.country === "US" && (
          <RpaDropdown
            required
            label="State"
            data-testid="state"
            placeholder="Select.."
            data={usStateSelectData}
            disabled={isLoading}
            searchable
            clearable={false}
            filterDataOnExactSearchMatch
            searchValue={stateSearchValue}
            onSearchChange={setStateSearchValue}
            nothingFound="No states found"
            {...form.getInputProps("state")}
          />
        )}
        <RpaDropdown
          required
          label="Country"
          data-testid="country"
          placeholder="Select.."
          data={countrySelectData}
          {...form.getInputProps("country")}
          disabled={isLoading}
          searchable
          clearable={false}
          filterDataOnExactSearchMatch
          searchValue={countrySearchValue}
          onSearchChange={setCountrySearchValue}
          nothingFound="No countries found"
        />

        <RpiCheckboxV2
          label="By saving your payment method, you agree to be charged for your orders daily."
          data-testid="termsCheckbox"
          {...form.getInputProps("agreeToTerms", { type: "checkbox", withError: true })}
          disabled={isLoading}
        />

        {isAccountSetup ? (
          <>
            {cardError && <RpiAlert data-testid="cardError">{cardError}</RpiAlert>}
            <RpaButtonPromo
              type="submit"
              form={FORM_ID}
              disabled={!!cardError || isLoading}
              isLoading={isLoading}
              data-testid="savePaymentMethod"
            >
              Save
            </RpaButtonPromo>
            <RpaNavLink className={classes.skipLink} to={getRoutePath("home")} data-testid="skipPaymentMethod">
              Skip
            </RpaNavLink>
          </>
        ) : (
          <RpaDrawer.Footer>
            <div className={classes.footer}>
              {cardError && <RpiAlert data-testid="cardError">{cardError}</RpiAlert>}
              <RpaButtonPromo
                type="submit"
                form={FORM_ID}
                fullWidth
                disabled={!!cardError || isLoading}
                isLoading={isLoading}
                data-testid="savePaymentMethod"
              >
                Save Changes
              </RpaButtonPromo>
              {onCancel && (
                <Button variant="text" intent="primary" type="button" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </div>
          </RpaDrawer.Footer>
        )}
      </>
    </form>
  );
};
